import React, { useRef, useState, useEffect } from "react";
import { useScreenDimensions, useDevice } from "../_layout";
import ClientDashboard from "../ClientViews/ClientDashboard";
import CoachSettings from "./CoachSettings";
import CoachLibrary from "./CoachLibrary/CoachLibrary";
import CoachBilling from "./CoachBilling/CoachBilling";
import CoachCompetitions from "./CoachCompetitions/CoachCompetitions";
import CoachApplications from "./CoachApplications/CoachApplications";
import InternalAnalytics from "../Components/InternalAnalytics";
import CoachDashboard from "./CoachDashboard";
import SideMenu from "../Components/SideMenu";
import {
  Text,
  View,
  TouchableOpacity,
  ScrollView,
  Platform,
} from "react-native";
import GlobalStyles from "../../Resources/Styles";
import Colors from "../../Resources/Color";
import Fonts from "../../Resources/Fonts";
import { useTranslation } from "react-i18next";
import { Feather, Ionicons, Ionicons as IoniconsLib } from "@expo/vector-icons";
import { Octicons } from "@expo/vector-icons";

import CustomAlert from "./../Components/CustomAlert";
import StripePayment from "./../CoachViews/Stripe/StripePayment";
import StripePaymentFailed from "./../CoachViews/Stripe/StripePaymentFailed";
import CustomDrawerModal from "../Components/DrawerSideMenu";
import SendFeedback from "../Components/SendFeedback";
import TutorialPanel from "../Components/TutorialPanel";
import OfflineAlert from "../Components/OfflineAlert";
import CustomActivityIndicator from "../Components/CustomActivityIndicator";

import { observer, Observer } from "mobx-react";

import ChatHover from "../Chat/ChatHover";
import CoachChat from "./CoachChat";
import BlockViewOverlay from "../Block/shared/BlockViewOverlay";

import CoachNutrition from "./CoachNutrition/CoachNutrition";
import AthleteAppOnboardingModal from "../Components/AthleteAppOnboardingModal";
import WhatsNewModal from "../Components/WhatsNewModal";
import { getAppVersion } from "../utils/version";
import { useToast } from "../contexts/ToastContext";
import {
  discardUnsavedLeaveDraft,
  getUnsavedLeaveGuardContext,
  UNSAVED_LEAVE_CONTEXTS,
} from "../platform/guards/unsavedLeaveGuard";
import { isWorkablePendingWorkspaceClient } from "../invitations/pendingWorkspaceState";

const MainView = ({ coach }) => {
  const { screenSize } = useScreenDimensions();
  const { isMobile } = useDevice();
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [showingUnsavedLeaveAlert, setShowingUnsavedLeaveAlert] =
    useState(false);
  const [unsavedLeaveContext, setUnsavedLeaveContext] = useState(null);
  const [profileDiscardResetVersion, setProfileDiscardResetVersion] =
    useState(0);
  const pendingNavigationActionRef = useRef(null);
  const coachSettingsLeaveHandlersRef = useRef(null);

  // Track visual viewport changes for keyboard handling on mobile web
  // Only adjust layout when chat list is showing and on mobile devices
  // TEMPORARILY DISABLED - uncomment the effect body to re-enable
  // useEffect(() => {
  //   return; // Disabled - remove this line to re-enable keyboard handling

  //   if (typeof window === "undefined" || !window.visualViewport || !isMobile) {
  //     return;
  //   }

  //   const updateKeyboardHeight = () => {
  //     if (!coach.showingChatList) {
  //       setKeyboardHeight(0);
  //       return;
  //     }

  //     const newKeyboardHeight = Math.max(
  //       0,
  //       window.innerHeight - window.visualViewport.height
  //     );
  //     setKeyboardHeight(newKeyboardHeight);
  //   };

  //   // Initial call
  //   updateKeyboardHeight();

  //   window.visualViewport.addEventListener("resize", updateKeyboardHeight);
  //   return () => {
  //     window.visualViewport.removeEventListener("resize", updateKeyboardHeight);
  //   };
  // }, [coach.showingChatList, isMobile]);

  const showingResetAlertTitle = "resetAthletesAlertTitle-string";
  const showingResetAlertText = "resetAthletesAlertText-string";

  const getShowExpandedChat = () =>
    !coach?.isOffline &&
    coach?.showingChatList &&
    coach?.chatIsExpanded &&
    screenSize !== "small";

  const resetIsCheckeds = () => {
    if (!coach.isLoading) {
      const sourceCoach = coach.teamCoach || coach;
      const clientsToReset = (sourceCoach?.clients || []).filter((client) => {
        if (client?.status === "active-string") {
          return true;
        }
        return isWorkablePendingWorkspaceClient(client);
      });

      clientsToReset.forEach((client) => {
        if (client.getProperty("isChecked") !== true) {
          return;
        }

        client.setProperty("isChecked", false);

        if (client.status === "active-string") {
          client.uploadData({
            is_checked: false,
          });
          return;
        }

        if (isWorkablePendingWorkspaceClient(client)) {
          client.persistPendingWorkspaceChecked(false).catch((error) => {
            client.setProperty("isChecked", true);
            console.warn(
              "[main-view] failed to reset pending invitation is_checked",
              client?.id,
              error
            );
          });
        }
      });
    }
  };

  const registerCoachSettingsUnsavedLeaveHandlers = (handlers) => {
    coachSettingsLeaveHandlersRef.current = handlers;
  };

  const requestProfileLeaveAwareNavigation = (action) => {
    if (!action) {
      return;
    }

    const selectedClient = coach?.selectedClient?.();
    const guardContext = getUnsavedLeaveGuardContext({
      coach,
      selectedClient,
    });

    if (!guardContext) {
      action();
      return;
    }

    pendingNavigationActionRef.current = action;
    setUnsavedLeaveContext(guardContext);
    setShowingUnsavedLeaveAlert(true);
  };

  const closeUnsavedLeaveAlert = () => {
    pendingNavigationActionRef.current = null;
    setShowingUnsavedLeaveAlert(false);
    setUnsavedLeaveContext(null);
  };

  const handleDiscardAndStay = () => {
    const currentGuardContext = unsavedLeaveContext;
    const selectedClient = coach?.selectedClient?.();

    const didDiscard = discardUnsavedLeaveDraft({
      guardContext: currentGuardContext,
      selectedClient,
      coachSettingsLeaveHandlers: coachSettingsLeaveHandlersRef.current,
    });

    if (
      didDiscard &&
      currentGuardContext === UNSAVED_LEAVE_CONTEXTS.CLIENT_PROFILE
    ) {
      setProfileDiscardResetVersion((currentVersion) => currentVersion + 1);
    }

    closeUnsavedLeaveAlert();
  };

  const handleCancelAndStay = () => {
    closeUnsavedLeaveAlert();
  };

  const unsavedLeaveAlertTitle =
    unsavedLeaveContext === UNSAVED_LEAVE_CONTEXTS.COACH_SETTINGS
      ? "coachSettingsUnsavedLeaveAlertTitle-string"
      : "profileUnsavedLeaveAlertTitle-string";
  const unsavedLeaveAlertText =
    unsavedLeaveContext === UNSAVED_LEAVE_CONTEXTS.COACH_SETTINGS
      ? "coachSettingsUnsavedLeaveAlertText-string"
      : "profileUnsavedLeaveAlertText-string";

  const FixedBar = () => {
    return (
      <View
        style={{
          height: 52,
          backgroundColor: "#1E1E1E",
          justifyContent: "space-between",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <TouchableOpacity
          style={{ padding: 10, marginLeft: 10 }}
          onPress={() => coach.setProperty("sideMenuVisible", true)}
        >
          <Feather name="menu" size={26} color={Colors.whiteColor} />
        </TouchableOpacity>
        {coach.shouldShowSubscriptionUpgrade && (
          <TouchableOpacity
            style={{ padding: 10, marginRight: 10 }}
            onPress={() => {
              coach.setProperty("showingStripePayment", true);
            }}
          >
            <Text style={[Fonts.medium14, { color: Colors.lightGrayColor }]}>
              {t("upgradePlan-string")}
            </Text>
          </TouchableOpacity>
        )}
      </View>
    );
  };

  return (
    <>
      {screenSize === "large" && (
        <SideMenu
          coach={coach}
          requestProfileLeaveAwareNavigation={requestProfileLeaveAwareNavigation}
        />
      )}

      <View
        style={{
          position: "absolute",
          left: screenSize === "large" ? 220 : 0,
          right: 0,
          top: 0,
          bottom: keyboardHeight,
        }}
      >
        {(screenSize === "small" || screenSize === "medium") && <FixedBar />}
        <Observer>
          {() => {
            const client = coach.selectedClient();
            const clientBlock = client?.selectedBlock;
            const libraryBlock = coach?.selectedBlock;
            const showExpandedChat = getShowExpandedChat();
            const isLegacyPlanViewEnabled = coach?.useLegacyPlanView === true;
            const isClientBlockViewActive = isLegacyPlanViewEnabled
              ? coach?.useLegacyPlanViewSelection === false
              : true;
            const hasClientExercisesInWorkouts = clientBlock?.workouts?.some(
              (workout) => (workout?.exercises?.length ?? 0) > 0,
            );
            const hasLibraryExercisesInWorkouts = libraryBlock?.workouts?.some(
              (workout) => (workout?.exercises?.length ?? 0) > 0,
            );
            const showClientOverlay =
              client &&
              client.selectedTab === "planification-string" &&
              clientBlock &&
              !clientBlock.isShowingDashboard &&
              !showExpandedChat &&
              hasClientExercisesInWorkouts &&
              isClientBlockViewActive;
            const showLibraryOverlay =
              coach?.showingCoachView === "CoachLibrary" &&
              coach?.librarySelectedTab === "blocks-string" &&
              libraryBlock &&
              !libraryBlock.isShowingDashboard &&
              !showExpandedChat &&
              hasLibraryExercisesInWorkouts;
            const showOverlay = showClientOverlay || showLibraryOverlay;
            const overlayBlock = showLibraryOverlay
              ? libraryBlock
              : clientBlock;
            const contentMaxWidth = 1360;
            const contentPaddingHorizontal = showExpandedChat
              ? 0
              : screenSize === "large"
                ? 24
                : screenSize === "medium"
                  ? 24
                  : 8;
            const contentPaddingTop = showExpandedChat
              ? 0
              : screenSize === "large"
                ? 24
                : screenSize === "medium"
                  ? 24
                  : 16;
            const contentMarginBottom = showExpandedChat
              ? 0
              : screenSize === "large"
                ? 64
                : screenSize === "medium"
                  ? 64
                  : 62;
            const overlayLeftInset =
              contentPaddingHorizontal + (screenSize === "small" ? 8 : 8);
            const scrollContainerStyle =
              Platform.OS === "web"
                ? { scrollbarGutter: "stable", overflowY: "scroll" }
                : undefined;

            return (
              <>
                <ScrollView
                  style={scrollContainerStyle}
                  contentContainerStyle={{
                    flexGrow: 1,
                  }}
                  showsVerticalScrollIndicator={true}
                >
                  <View
                    style={{
                      flex: 1,
                      width: "100%",
                      maxWidth: contentMaxWidth,
                      alignSelf: "center",
                    }}
                  >
                    <Observer>
                      {() => {
                        return (
                          <View
                            style={{
                              flex: 1,
                              width: "100%",
                              paddingTop: contentPaddingTop,
                              paddingHorizontal: contentPaddingHorizontal,
                              marginBottom: contentMarginBottom,
                            }}
                          >
                            {showExpandedChat ? (
                              <CoachChat coach={coach} />
                            ) : coach &&
                              coach.selectedClient() &&
                              coach.showingCoachView === "ClientDashboard" ? (
                              <ClientDashboard
                                client={coach.selectedClient()}
                                requestProfileLeaveAwareNavigation={
                                  requestProfileLeaveAwareNavigation
                                }
                                profileDiscardResetVersion={
                                  profileDiscardResetVersion
                                }
                              />
                            ) : coach.showingCoachView === "CoachSettings" ? (
                              <CoachSettings
                                coach={coach}
                                registerUnsavedLeaveHandlers={
                                  registerCoachSettingsUnsavedLeaveHandlers
                                }
                              />
                            ) : coach.showingCoachView === "CoachLibrary" ? (
                              <CoachLibrary coach={coach} />
                            ) : coach.showingCoachView === "CoachBilling" ? (
                              <CoachBilling coach={coach} />
                            ) : coach.showingCoachView ===
                              "CoachCompetitions" ? (
                              <CoachCompetitions
                                coach={
                                  coach.teamCoach ? coach.teamCoach : coach
                                }
                              />
                            ) : coach.showingCoachView ===
                              "CoachApplications" ? (
                              <CoachApplications coach={coach} />
                            ) : coach.showingCoachView ===
                              "InternalAnalytics" ? (
                              <InternalAnalytics />
                            ) : coach.showingCoachView === "CoachNutrition" ? (
                              <CoachNutrition coach={coach} />
                            ) : coach ? (
                              <CoachDashboard
                                coach={
                                  coach.teamCoach ? coach.teamCoach : coach
                                }
                                showClient={() =>
                                  coach.setProperty(
                                    "showingCoachView",
                                    "ClientDashboard",
                                  )
                                }
                              />
                            ) : (
                              <View
                                style={{
                                  flex: 1,
                                  alignItems: "center",
                                  justifyContent: "space-around",
                                }}
                              >
                                <CustomActivityIndicator
                                  large={false}
                                  white={false}
                                />
                              </View>
                            )}
                            {coach.showingAthleteAppOnboarding && (
                              <AthleteAppOnboardingModal
                                isVisible={coach.showingAthleteAppOnboarding}
                                onClose={() =>
                                  coach.setProperty(
                                    "showingAthleteAppOnboarding",
                                    false,
                                  )
                                }
                                onCompleted={() => {
                                  if (coach.onboardingShowAthleteApp) {
                                    coach.setProperty(
                                      "onboardingShowAthleteApp",
                                      false,
                                    );
                                    coach.updateCoachPublicProfile({
                                      onboarding_show_athlete_app: false,
                                    });
                                    showToast("step1Completed-string", {
                                      type: "info",
                                      description:
                                        "step1CompletedDescription-string",
                                    });
                                  }
                                }}
                                email={coach.email}
                                hideCloseButton={coach.onboardingShowAthleteApp}
                                isAlreadyCompleted={
                                  !coach.onboardingShowAthleteApp
                                }
                              />
                            )}
                            {coach.showingSendFeedback && (
                              <SendFeedback
                                isVisible={coach.showingSendFeedback}
                                onClose={() =>
                                  coach.setProperty(
                                    "showingSendFeedback",
                                    false,
                                  )
                                }
                                coach={coach}
                              />
                            )}
                            {coach.showingWhatsNew && (
                              <WhatsNewModal
                                isVisible={coach.showingWhatsNew}
                                onClose={() =>
                                  coach.setProperty("showingWhatsNew", false)
                                }
                                onSendFeedback={() =>
                                  coach.setProperty("showingSendFeedback", true)
                                }
                                onPrimaryPress={() => {
                                  const currentVersion = getAppVersion();
                                  coach.setProperty(
                                    "lastSeenVersion",
                                    currentVersion,
                                  );
                                  coach.uploadData({
                                    last_seen_version: currentVersion,
                                  });
                                  coach.setProperty("showingWhatsNew", false);
                                }}
                              />
                            )}
                            {coach.showingStripePayment && (
                              <StripePayment
                                isVisible={coach.showingStripePayment}
                                onClose={() =>
                                  coach.setProperty(
                                    "showingStripePayment",
                                    false,
                                  )
                                }
                                coach={coach}
                              />
                            )}
                            {coach.showingResetAlert && (
                              <CustomAlert
                                isVisible={coach.showingResetAlert}
                                onClose={() =>
                                  coach.setProperty("showingResetAlert", false)
                                }
                                title={showingResetAlertTitle}
                                text={showingResetAlertText}
                                action={resetIsCheckeds}
                                specialCTA="reset-string"
                              />
                            )}
                            {coach.showingPaymentFailed && (
                              <StripePaymentFailed
                                isVisible={coach.showingPaymentFailed}
                                onClose={() =>
                                  console.log("Subscription On Hold")
                                }
                                coach={coach}
                              />
                            )}
                            {coach.showingSubscriptionEnded && (
                              <StripePayment
                                isVisible={coach.showingSubscriptionEnded}
                                onClose={() =>
                                  console.log("Subscription Ended")
                                }
                                coach={coach}
                                subscriptionEnded={true}
                              />
                            )}
                            {coach.showingOfflineAlert && (
                              <OfflineAlert
                                isVisible={coach.showingOfflineAlert}
                                onClose={() =>
                                  coach.setProperty(
                                    "showingOfflineAlert",
                                    false,
                                  )
                                }
                              />
                            )}
                            {showingUnsavedLeaveAlert && (
                              <CustomAlert
                                isVisible={showingUnsavedLeaveAlert}
                                onClose={handleCancelAndStay}
                                title={unsavedLeaveAlertTitle}
                                text={unsavedLeaveAlertText}
                                action={handleDiscardAndStay}
                                specialCTA="discardChanges-string"
                              />
                            )}
                          </View>
                        );
                      }}
                    </Observer>
                  </View>
                </ScrollView>
                {/* BlockView Overlay - rendered outside ScrollView but inside Observer */}
                {showOverlay && (
                  <View
                    pointerEvents="box-none"
                    style={{
                      position: "absolute",
                      top: 0,
                      bottom: 0,
                      left: 0,
                      right: 0,
                      alignItems: "center",
                      zIndex: 50,
                    }}
                  >
                    <View
                      pointerEvents="box-none"
                      style={{
                        width: "100%",
                        maxWidth: contentMaxWidth,
                        alignSelf: "center",
                        paddingHorizontal: contentPaddingHorizontal,
                        height: "100%",
                        position: "relative",
                      }}
                    >
                      <BlockViewOverlay
                        block={overlayBlock}
                        leftInset={overlayLeftInset}
                      />
                    </View>
                  </View>
                )}
              </>
            );
          }}
        </Observer>

        <Observer>
          {() => {
            const showExpandedChat = getShowExpandedChat();
            return !showExpandedChat && <TutorialPanel coach={coach} />;
          }}
        </Observer>
      </View>

      <Observer>
        {() =>
          !coach.isOffline && (
            <ChatHover coach={coach} keyboardHeight={keyboardHeight} />
          )
        }
      </Observer>
      <CustomDrawerModal
        coach={coach}
        requestProfileLeaveAwareNavigation={requestProfileLeaveAwareNavigation}
      />
    </>
  );
};

export default MainView;
